import { useMemo } from 'react';

/* eslint-disable @typescript-eslint/no-explicit-any */
import type { VerificationStatus } from '@hotjar/webapp/domains/activation/tracking-code/types';
import type { AccountDTO as Account } from '@hotjar/account';
import { PAYMENT_ORIGINS } from '@hotjar/webapp/domains/billing/constants';
import type { SegmentTrackData } from '@hotjar/webapp/domains/common/tracking/segment';
import {
  reset,
  getAnonymousUserID,
  setAnonymousUserID,
} from '@hotjar/webapp/domains/common/tracking/segment';
import type { SurveyTemplateSearchedArgs } from '@hotjar/webapp/tracking/common';
import type { SignupTrackingData } from '@hotjar/tracking/src/signup-step-progress';
import {
  closedModal,
  companyDetailsFormSubmitted,
  download,
  experimentViewed,
  itemShared,
  formSubmit,
  loadedModal,
  optimizelyIntegration,
  tagRecording,
  trackClick,
  trackingCodeInstallerOpened,
  trackingCodeVerificationAttempt,
  trackingCodeVerificationRequest,
  viewElement,
  withContext,
  onboardingStarted,
  onboardingEnded,
  surveyTemplateSearched,
  logoutIfAlreadyIdentified,
} from '@hotjar/webapp/tracking/common';
import { clickedTrackingActive, clickedUpsell } from '@hotjar/webapp/tracking/header';
import * as googleAnalytics from '@hotjar/webapp/tracking/tools/google-analytics';
import * as facebook from '@hotjar/webapp/tracking/tools/facebook';
import {
  extractAccountGroupingProps,
  extractSiteGroupingProps,
  extractPermissionLevel,
} from '@hotjar/webapp/tracking/utils';
import type {
  TrackingSite,
  SiteAccess,
  SiteGroupingData,
  AccountGroupingData,
  PermissionLevelLabel,
} from '@hotjar/webapp/tracking/utils';

import type { UserCreatedData } from '@hotjar/tracking';
import {
  alias,
  userCreated,
  userCreatedError,
  signupStepLoaded,
  signupStepSubmitted,
  signupStepCompleted,
  emailVerificationRequest,
  emailVerificationAttempted,
} from '@hotjar/tracking';
import { useCurrentSite } from '@hotjar/site';
import type { Site } from '@hotjar/site';
import { useCurrentUser } from '@hotjar/user';

export interface TrackFunctions {
  alias: (userId: number) => Promise<void>;
  // FIXME: Please fix this. You may refer to CFE-266 for more details.
  // eslint-disable-next-line func-params-args/func-params
  download: (item: string, fileType: string, groupData: any, additionalData?: any) => void;
  add(item: 'Organization' | 'Site'): void;
  tagRecording: (tagName: string) => void;
  // FIXME: Please fix this. You may refer to CFE-266 for more details.
  // eslint-disable-next-line func-params-args/func-params
  click: (eventName: string, uiPosition: string, groupData: any, additionalData?: any) => void;
  // FIXME: Please fix this. You may refer to CFE-266 for more details.
  // eslint-disable-next-line func-params-args/func-params
  viewElement: (
    eventName: string,
    uiPosition: string,
    groupData: any,
    additionalData?: any,
  ) => void;
  // FIXME: Please fix this. You may refer to CFE-266 for more details.
  // eslint-disable-next-line func-params-args/func-params
  formSubmit: (
    formName: string,
    submitSuccess: boolean,
    groupData: any,
    additionalData?: any,
  ) => Promise<any>;
  // FIXME: Please fix this. You may refer to CFE-266 for more details.
  // eslint-disable-next-line func-params-args/func-params
  itemShared: (data: {
    item?: string;
    recordingId?: number;
    relevanceScore?: number;
    sharedStatus?: 'On' | 'Off';
  }) => Promise<any>;
  withContext: (context: Record<string, unknown>, fn: () => Promise<unknown>) => Promise<unknown>;
  clickedUpsell: () => void;
  getGroupDataForAccount: (account: Account) => AccountGroupingData | undefined;
  getGroupDataForSite: (site: TrackingSite) => SiteGroupingData | undefined;
  getPermissionLevelForSite: (site: SiteAccess) => PermissionLevelLabel | undefined;
  clickedTrackingActive: () => void;
  optimizelyIntegration: (step: string) => void;
  paymentOrigins: Record<string, string>;
  experimentAssigned: (experimentId: string, experimentTargetId: string, variationId: any) => void;
  // FIXME: Please fix this. You may refer to CFE-266 for more details.
  // eslint-disable-next-line func-params-args/func-params
  experimentViewed: (
    experimentId: string,
    experimentTargetId: string,
    variationId: any,
    userId?: any,
  ) => void;
  loadedModal: (
    name: string,
    groupData: AccountGroupingData | SiteGroupingData | undefined,
    data?: SegmentTrackData,
  ) => void;
  closedModal: (
    name: string,
    groupData: AccountGroupingData | SiteGroupingData | undefined,
    data?: SegmentTrackData,
  ) => void;
  hotjarInstallerOpened: (
    site: TrackingSite,
    source: string,
    action: 'install' | 'verify' | 'add-url',
  ) => void;
  trackTCVerificationRequest: (site: TrackingSite) => void;
  trackTCVerificationAttempt: (site: TrackingSite, status: VerificationStatus) => void;
  companyDetailsFormSubmitted: (params: {
    success: boolean;
    failureReason?: string;
    taxFieldPresent: boolean;
    taxFieldFilled: boolean;
    countrySelectChanged: boolean;
  }) => void;
  signupStepLoaded: (data: SignupTrackingData) => void;
  signupStepSubmitted: (data: SignupTrackingData) => void;
  signupStepCompleted: (data: SignupTrackingData) => void;
  emailVerificationRequest: () => void;
  emailVerificationAttempted: (isValid: boolean, method: string) => void;
  login: () => void;
  logout: () => void;
  logoutIfAlreadyIdentified: () => void;
  resetAndKeepAnonymousId: () => void;
  onboardingStarted: (site: Site) => void;
  onboardingEnded: () => void;
  surveyTemplateSearched: (params: SurveyTemplateSearchedArgs) => void;
  getAnonymousUserID: () => Promise<string | unknown>;
  userCreated: (data: UserCreatedData) => Promise<void>;
  userCreatedError: (err: string) => Promise<void>;
}

export const useTrack = (): TrackFunctions => {
  const { data: currentSite } = useCurrentSite();
  const { data: currentUser } = useCurrentUser();

  return useMemo(() => {
    return {
      alias,

      tagRecording: (tagName) => tagRecording({ name: tagName }),

      // FIXME: Please fix this. You may refer to CFE-266 for more details.
      // eslint-disable-next-line func-params-args/func-params
      click: (eventName, uiPosition, groupData, additionalData) =>
        trackClick({ name: eventName, uiPosition, groupData, data: additionalData }),

      add: (item) => googleAnalytics.trackCreatedItemEvent(`Added ${item}`),

      // FIXME: Please fix this. You may refer to CFE-266 for more details.
      // eslint-disable-next-line func-params-args/func-params
      download: (item, fileType, groupData, additionalData) =>
        download({ item, fileType, groupData, data: additionalData }),

      // FIXME: Please fix this. You may refer to CFE-266 for more details.
      // eslint-disable-next-line func-params-args/func-params
      viewElement: (eventName, uiPosition, groupData, additionalData) =>
        viewElement({ name: eventName, uiPosition, groupData, data: additionalData }),

      // FIXME: Please fix this. You may refer to CFE-266 for more details.
      // eslint-disable-next-line func-params-args/func-params
      formSubmit: (name, submitSuccess, groupData, data) =>
        formSubmit({ name, submitSuccess, groupData, data }),

      itemShared: (data) => (currentSite ? itemShared(data, currentSite) : Promise.resolve()),

      withContext,

      paymentOrigins: PAYMENT_ORIGINS,

      clickedUpsell,

      getGroupDataForAccount: extractAccountGroupingProps,

      getGroupDataForSite: extractSiteGroupingProps,

      getPermissionLevelForSite: extractPermissionLevel,

      clickedTrackingActive,

      optimizelyIntegration: (step) => {
        if (currentSite) {
          optimizelyIntegration(step, currentSite);
        }
      },

      // FIXME: Please fix this. You may refer to CFE-266 for more details.
      // eslint-disable-next-line func-params-args/func-params
      experimentViewed: (experimentId, experimentTargetId, variationId, userId) => {
        experimentViewed({
          experimentId,
          experimentTargetId,
          variationId,
          userId,
          site: currentSite,
        });
      },

      experimentAssigned: (experimentId, experimentTargetId, variationId) => {
        experimentViewed({
          experimentId,
          experimentTargetId,
          variationId,
          userId: String(currentUser?.id),
          site: currentSite,
        });
      },

      onboardingStarted: (site: Site) =>
        onboardingStarted({ userId: String(currentUser?.id), site: site || currentSite }),

      onboardingEnded: () =>
        onboardingEnded({ userId: String(currentUser?.id), site: currentSite }),

      loadedModal,

      closedModal,

      trackTCVerificationRequest: trackingCodeVerificationRequest,

      trackTCVerificationAttempt: trackingCodeVerificationAttempt,

      hotjarInstallerOpened: (site, source, action) =>
        trackingCodeInstallerOpened(site, {
          source,
          action,
        }),

      companyDetailsFormSubmitted: (params) => {
        if (currentSite) {
          companyDetailsFormSubmitted(params, currentSite.account);
        }
      },

      signupStepSubmitted,

      signupStepCompleted,

      signupStepLoaded,

      emailVerificationRequest,

      emailVerificationAttempted,

      login: () => facebook.trackLogin(),

      logout: () => reset(),

      logoutIfAlreadyIdentified: () => logoutIfAlreadyIdentified(),

      resetAndKeepAnonymousId: async () => {
        const id = await getAnonymousUserID();
        reset();
        setAnonymousUserID(id);
      },
      surveyTemplateSearched,

      getAnonymousUserID,

      userCreated,

      userCreatedError,
    };
  }, [currentSite, currentUser]);
};
